import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['draft', 'trialing', 'active', 'past_due', 'canceled', 'expired'];
const CYCLES = ['monthly', 'quarterly', 'annual', 'one_time', 'custom'];

function statusColor(s) {
  if (s === 'active' || s === 'trialing') return 'success';
  if (s === 'past_due') return 'warning';
  if (s === 'canceled' || s === 'expired') return 'default';
  return 'info';
}

export default function PlatformTenantSubscriptionsListPage() {
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [planInput, setPlanInput] = useState('');
  const [appliedPlanId, setAppliedPlanId] = useState('');
  const [cycleInput, setCycleInput] = useState('');
  const [appliedCycle, setAppliedCycle] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [plans, setPlans] = useState([]);
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tr, pr] = await Promise.all([
          platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc'),
          platformApiFetch('/api/platform/plans?pageSize=200&sort=name&order=asc')
        ]);
        const tj = await tr.json().catch(() => ({}));
        const pj = await pr.json().catch(() => ({}));
        if (!cancelled) {
          if (tr.ok) setTenants(tj.tenants || []);
          if (pr.ok) setPlans(pj.plans || []);
        }
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedPlanId) params.set('planId', appliedPlanId);
    if (appliedTenantId) params.set('tenantId', appliedTenantId);
    if (appliedCycle) params.set('billingCycle', appliedCycle);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-subscriptions?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load subscriptions');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedPlanId, appliedTenantId, appliedCycle, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedPlanId(planInput);
    setAppliedTenantId(tenantInput);
    setAppliedCycle(cycleInput);
  };

  const rows = data?.subscriptions || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tenant subscriptions</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/tenant-subscriptions/new')}>
            New subscription
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assign and lifecycle-manage subscriptions. Activating a live subscription supersedes other live rows for the
        same tenant and updates <code>tenants.plan_id</code>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={tenantInput} onChange={(e) => setTenantInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.tenant_name} ({t.tenant_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Plan</InputLabel>
            <Select label="Plan" value={planInput} onChange={(e) => setPlanInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {plans.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Billing</InputLabel>
            <Select label="Billing" value={cycleInput} onChange={(e) => setCycleInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {CYCLES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Created</MenuItem>
              <MenuItem value="start_date">Start</MenuItem>
              <MenuItem value="renewal_date">Renewal</MenuItem>
              <MenuItem value="subscription_status">Status</MenuItem>
              <MenuItem value="billing_cycle">Billing cycle</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>Renewal</TableCell>
                  <TableCell>Price snap.</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2">{s.tenant_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.tenant_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.plan_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.plan_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.subscription_status} color={statusColor(s.subscription_status)} />
                    </TableCell>
                    <TableCell>{s.billing_cycle}</TableCell>
                    <TableCell>{s.start_date || '—'}</TableCell>
                    <TableCell>{s.renewal_date || '—'}</TableCell>
                    <TableCell>{s.price_snapshot != null ? s.price_snapshot.toFixed(2) : '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(`/platform/tenant-subscriptions/${s.id}`)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSuper && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/platform/tenant-subscriptions/${s.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary">No subscriptions found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
